import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import { TableColumn } from '../../types';
import CrewEvaluationForm from '../../components/HR/CrewEvaluationForm';
import { CrewEvaluation } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import type { FilterConfig } from '../../components/Common';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { useCrewEvaluations, useCreateCrewEvaluation, useUpdateCrewEvaluation, useDeleteCrewEvaluation } from '../../hooks/useCrewEvaluations';
import TableSkeleton from '../../components/Loading/TableSkeleton';

const CrewEvaluationPage: React.FC = () => {
  const { user, hasPermission } = useAuth();

  const queryOptions = useMemo(() => ({
    pagination: { page: 1, pageSize: 1000 },
    sort: [{ field: 'createdAt', order: 'desc' as const }]
  }), []);

  const { data: evaluations = [], isLoading, error, refetch } = useCrewEvaluations(queryOptions);

  const createMutation = useCreateCrewEvaluation(() => { refetch(); });
  const updateMutation = useUpdateCrewEvaluation(() => { refetch(); });
  const deleteMutation = useDeleteCrewEvaluation(() => { refetch(); });

  const [showForm, setShowForm] = useState(false);
  const [editingEvaluation, setEditingEvaluation] = useState<CrewEvaluation | undefined>();
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [evaluatorFilter, setEvaluatorFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredEvaluations = useMemo(() => {
    let filtered = evaluations;
    if (user?.role?.name === 'Vessel Manager') {
      filtered = filtered.filter(e => e.evaluatorRole === 'vessel_manager');
    } else if (user?.role?.name === 'Chief Manager') {
      filtered = filtered.filter(e => e.evaluatorRole === 'chief_manager');
    }
    if (statusFilter !== 'all') filtered = filtered.filter(e => e.status === statusFilter);
    if (evaluatorFilter !== 'all') filtered = filtered.filter(e => e.evaluatorRole === evaluatorFilter);
    if (searchTerm) {
      filtered = filtered.filter(e =>
        e.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.evaluatorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.vesselName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [evaluations, statusFilter, evaluatorFilter, searchTerm, user?.role]);

  const handleCreateEvaluation = () => { setEditingEvaluation(undefined); setShowForm(true); };
  const handleEditEvaluation = (evaluation: CrewEvaluation) => { setEditingEvaluation(evaluation); setShowForm(true); };
  const handleDeleteEvaluation = async (evaluation: CrewEvaluation) => {
    if (window.confirm('Are you sure you want to delete this evaluation?')) {
      await deleteMutation.execute(evaluation);
    }
  };

  const handleSaveEvaluation = async (evaluationData: Partial<CrewEvaluation>) => {
    try {
      if (editingEvaluation) {
        await updateMutation.execute({ id: editingEvaluation.id, data: { ...evaluationData, documentId: (editingEvaluation as any).documentId } });
      } else {
        await createMutation.execute(evaluationData);
      }
      setShowForm(false);
      setEditingEvaluation(undefined);
    } catch {
      // Keep form open on error
    }
  };

  const handleApproveEvaluation = async (evaluation: CrewEvaluation) => {
    await updateMutation.execute({
      id: evaluation.id,
      data: {
        ...evaluation,
        status: 'approved',
        approvedBy: `${user?.firstName} ${user?.lastName}` || 'Top Management',
        approvedAt: new Date().toISOString()
      }
    });
  };

  const handleRejectEvaluation = async (evaluation: CrewEvaluation) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason) {
      await updateMutation.execute({
        id: evaluation.id,
        data: { ...evaluation, status: 'rejected', rejectionReason: reason }
      });
    }
  };

  const handleExport = () => {
    const exportData = filteredEvaluations.map(e => ({
      'Employee Name': e.employeeName, 'Vessel Name': e.vesselName || 'N/A',
      'Evaluation Period Start': e.evaluationPeriod?.fromDate ? new Date(e.evaluationPeriod.fromDate).toLocaleDateString() : 'N/A',
      'Evaluation Period End': e.evaluationPeriod?.toDate ? new Date(e.evaluationPeriod.toDate).toLocaleDateString() : 'N/A',
      'Overall Rating': e.overallRating, 'Status': e.status?.charAt(0).toUpperCase() + e.status?.slice(1),
      'Evaluator Name': e.evaluatorName, 'Evaluator Role': e.evaluatorRole,
      'Overall Comments': e.overallComments || 'N/A', 'Categories Count': e.categories?.length || 0,
      'Created Date': e.createdAt ? new Date(e.createdAt).toLocaleDateString() : 'N/A',
      'Updated Date': e.updatedAt ? new Date(e.updatedAt).toLocaleDateString() : 'N/A',
      'Approved By': e.approvedBy || 'N/A', 'Approved Date': e.approvedAt ? new Date(e.approvedAt).toLocaleDateString() : 'N/A',
      'Rejection Reason': e.rejectionReason || 'N/A'
    }));
    exportToExcel(exportData, `crew_evaluations_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePrint = () => {
    const exportData = filteredEvaluations.map(e => ({
      'Employee Name': e.employeeName, 'Vessel Name': e.vesselName || 'N/A',
      'Evaluation Period Start': e.evaluationPeriod?.fromDate ? new Date(e.evaluationPeriod.fromDate).toLocaleDateString() : 'N/A',
      'Evaluation Period End': e.evaluationPeriod?.toDate ? new Date(e.evaluationPeriod.toDate).toLocaleDateString() : 'N/A',
      'Overall Rating': e.overallRating, 'Status': e.status?.charAt(0).toUpperCase() + e.status?.slice(1),
      'Evaluator Name': e.evaluatorName, 'Evaluator Role': e.evaluatorRole,
      'Overall Comments': e.overallComments || 'N/A', 'Categories Count': e.categories?.length || 0,
      'Created Date': e.createdAt ? new Date(e.createdAt).toLocaleDateString() : 'N/A',
      'Updated Date': e.updatedAt ? new Date(e.updatedAt).toLocaleDateString() : 'N/A'
    }));
    printAsExcel(exportData, 'Crew Evaluations');
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setEvaluatorFilter('all');
    setSearchTerm('');
  };

  const getStatusBadge = (status: string) => {
    const statusStyles: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800', submitted: 'bg-blue-100 text-blue-800',
      approved: 'bg-green-100 text-green-800', rejected: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status ? status.charAt(0).toUpperCase() + status.slice(1) : 'N/A'}
      </span>
    );
  };

  const getRatingBadge = (rating: number) => {
    const color = rating >= 4.5 ? 'bg-green-100 text-green-800' : rating >= 3.5 ? 'bg-blue-100 text-blue-800' : rating >= 2.5 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800';
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${color}`}>{(rating || 0).toFixed(1)}</span>;
  };

  const columns: TableColumn[] = [
    { key: 'employeeName', label: 'Employee', sortable: true },
    { key: 'evaluatorName', label: 'Evaluator', sortable: true, render: (value: string, item: CrewEvaluation) => (
      <div>
        <div className="font-medium">{value}</div>
        <div className="text-sm text-gray-500 capitalize">{item.evaluatorRole === 'hr' ? 'HR' : item.evaluatorRole?.replace('_', ' ')}</div>
      </div>
    ) },
    { key: 'vesselName', label: 'Vessel', sortable: true, render: (value: string) => value || 'N/A' },
    { key: 'evaluationPeriod', label: 'Evaluation Period', render: (value: any) => (
      <div className="text-sm">{value?.fromDate ? new Date(value.fromDate).toLocaleDateString() : 'N/A'} - {value?.toDate ? new Date(value.toDate).toLocaleDateString() : 'N/A'}</div>
    ) },
    { key: 'overallRating', label: 'Overall Rating', sortable: true, render: (value: number) => getRatingBadge(value) },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => getStatusBadge(value) },
    { key: 'approvedBy', label: 'Reviewed By', render: (_: string, item: CrewEvaluation) => (
      <div className="text-sm">
        {item.approvedBy ? <div className="font-medium">{item.approvedBy}</div> : <span className="text-gray-400">-</span>}
        {item.status === 'rejected' && item.rejectionReason ? (
          <div className="text-xs text-red-600 max-w-[200px] truncate" title={item.rejectionReason}>{item.rejectionReason}</div>
        ) : null}
      </div>
    ) },
    { key: 'createdAt', label: 'Created Date', sortable: true, render: (value: string) => value ? new Date(value).toLocaleDateString() : 'N/A' }
  ];

  const approvalColumn: TableColumn = {
    key: 'actions_custom',
    label: 'Approval',
    render: (_: any, item: CrewEvaluation) => item.status === 'submitted' ? (
      <div className="flex space-x-2">
        <button onClick={() => handleApproveEvaluation(item)} className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded hover:bg-green-200">Approve</button>
        <button onClick={() => handleRejectEvaluation(item)} className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded hover:bg-red-200">Reject</button>
      </div>
    ) : null
  };

  // Anyone who can open the module may create an evaluation; the evaluator
  // role recorded on the record distinguishes vessel/chief-manager vs HR.
  const canCreateEvaluation = hasPermission('crew-evaluation');
  // HR runs the evaluation process operationally, so they see the
  // approve/reject actions alongside management.
  const approverRoles = ['Super Admin', 'Admin', 'Top Management', 'HR', 'HR Manager', 'Human Resources'];
  const showApprovalColumn = approverRoles.includes(user?.role?.name || '');

  const filterConfigs: FilterConfig[] = [
    { key: 'status', label: 'Status', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'All Statuses' }, { value: 'draft', label: 'Draft' }, { value: 'submitted', label: 'Submitted' },
      { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }
    ]},
    { key: 'evaluator', label: 'Evaluator Type', type: 'select', value: evaluatorFilter, onChange: setEvaluatorFilter, options: [
      { value: 'all', label: 'All Evaluators' }, { value: 'vessel_manager', label: 'Vessel Manager' }, { value: 'chief_manager', label: 'Chief Manager' }, { value: 'hr', label: 'HR' }
    ]},
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Crew Evaluation Management" description="Evaluate crew performance and track approvals" />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Evaluations</p>
              <p className="text-2xl font-bold text-gray-900">{evaluations.length}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-yellow-100">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending Approval</p>
              <p className="text-2xl font-bold text-gray-900">{evaluations.filter(e => e.status === 'submitted').length}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Approved</p>
              <p className="text-2xl font-bold text-gray-900">{evaluations.filter(e => e.status === 'approved').length}</p>
            </div>
          </div>
        </div>
        <div className="card p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Average Rating</p>
              <p className="text-2xl font-bold text-gray-900">
                {evaluations.length > 0 ? (evaluations.reduce((sum, e) => sum + (e.overallRating || 0), 0) / evaluations.length).toFixed(1) : '0.0'}
              </p>
            </div>
          </div>
        </div>
      </div>
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search evaluations..."
        filters={filterConfigs}
        onClearFilters={handleClearFilters}
        totalCount={evaluations.length}
        filteredCount={filteredEvaluations.length}
        entityName="Crew Evaluations"
        onAdd={canCreateEvaluation ? handleCreateEvaluation : undefined}
        addLabel="New Evaluation"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <div className="card">
        {isLoading ? (
          <TableSkeleton />
        ) : error ? (
          <ErrorState message={error.message || String(error)} onRetry={() => refetch()} entityName="evaluations" />
        ) : (
          <Table
            data={filteredEvaluations}
            columns={showApprovalColumn ? [...columns, approvalColumn] : columns}
            onEdit={handleEditEvaluation}
            onDelete={handleDeleteEvaluation}
            searchable={false}
          />
        )}
      </div>
      {showForm && (
        <CrewEvaluationForm
          evaluation={editingEvaluation}
          onSave={handleSaveEvaluation}
          onCancel={() => { setShowForm(false); setEditingEvaluation(undefined); }}
          evaluatorRole={user?.role?.name === 'Vessel Manager' ? 'vessel_manager' : user?.role?.name === 'Chief Manager' ? 'chief_manager' : 'hr'}
          evaluatorName={`${user?.firstName} ${user?.lastName}` || 'Current User'}
          evaluatorId={user?.id || 'current-user'}
        />
      )}
    </div>
  );
};

export default CrewEvaluationPage;
